/**
 * Platform icon utility for automatically setting project covers based on project name
 * Uses Iconify API for colored Simple Icons brand icons
 */

export interface PlatformInfo {
  name: string;
  iconUrl: string;
  color: string;
}

/**
 * Map of platform keywords to platform identifiers
 */
const PLATFORM_KEYWORDS: Record<string, string> = {
  instagram: 'instagram',
  ig: 'instagram',
  'instagram.com': 'instagram',
  tiktok: 'tiktok',
  'tiktok.com': 'tiktok',
  youtube: 'youtube',
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  shorts: 'youtube',
  twitter: 'twitter',
  'twitter.com': 'twitter',
  x: 'twitter',
  facebook: 'facebook',
  'facebook.com': 'facebook',
  fb: 'facebook',
  linkedin: 'linkedin',
  'linkedin.com': 'linkedin',
  snapchat: 'snapchat',
  'snapchat.com': 'snapchat',
  pinterest: 'pinterest',
  'pinterest.com': 'pinterest',
  reddit: 'reddit',
  'reddit.com': 'reddit'
};

/**
 * Platform brand colors (used for Simple Icons)
 */
const PLATFORM_COLORS: Record<string, string> = {
  instagram: 'E4405F',
  tiktok: '000000',
  youtube: 'FF0000',
  twitter: '1DA1F2',
  facebook: '1877F2',
  linkedin: '0077B5',
  snapchat: 'FFFC00',
  pinterest: 'BD081C',
  reddit: 'FF4500'
};

/**
 * Detect platform from project name or slug
 * Returns platform identifier if detected, null otherwise
 */
export function detectPlatformFromName(name: string): string | null {
  if (!name) return null;

  const normalized = name.toLowerCase().trim();

  // Check for exact matches first
  for (const [keyword, platform] of Object.entries(PLATFORM_KEYWORDS)) {
    if (normalized === keyword || normalized.includes(keyword)) {
      return platform;
    }
  }

  // Check for word boundaries (e.g., "My TikTok Project" -> tiktok)
  for (const [keyword, platform] of Object.entries(PLATFORM_KEYWORDS)) {
    // Match whole words only
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    if (regex.test(normalized)) {
      return platform;
    }
  }

  return null;
}

/**
 * Get platform icon URL using jsDelivr CDN (most reliable)
 * Format: https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/{platform}.svg
 * Colors are applied via CSS filters in the component
 */
export function getPlatformIconUrl(platform: string): string | null {
  if (!platform) return null;

  const normalizedPlatform = platform.toLowerCase();

  if (!PLATFORM_COLORS[normalizedPlatform]) {
    return null;
  }

  // Use jsDelivr CDN for Simple Icons SVGs (most reliable)
  // Colors will be applied via CSS filters in PlatformCard component
  return `https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/${normalizedPlatform}.svg`;
}

const PLATFORM_ICON_URL_PREFIX = 'https://cdn.jsdelivr.net/npm/simple-icons@';

/**
 * True for a platform mark from `getPlatformIconUrl` (a single-colour black Simple Icons SVG).
 * Such a mark renders through PlatformGlyph (DESIGN.md Platform glyphs), never as an <img>; a
 * project that stored it as its cover_url gets the same treatment (cas-8538).
 */
export function isPlatformIconUrl(url: string | null | undefined): boolean {
  return (
    typeof url === 'string' && url.startsWith(PLATFORM_ICON_URL_PREFIX) && url.endsWith('.svg')
  );
}

/**
 * Get platform info (name and icon URL) from project name
 * Returns null if no platform detected
 */
export function getPlatformInfoFromName(name: string): PlatformInfo | null {
  const platform = detectPlatformFromName(name);

  if (!platform) {
    return null;
  }

  const iconUrl = getPlatformIconUrl(platform);

  if (!iconUrl) {
    return null;
  }

  return {
    name: platform,
    iconUrl,
    color: PLATFORM_COLORS[platform] || '000000'
  };
}
